/**
 * Software catalog page with filtering, search, and infinite scroll
 */

import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ShieldCheckIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { useGetSoftwareListQuery, useGetManufacturersQuery } from '@/services/api';

const categories = [
  'All',
  'Browser',
  'Database',
  'Office Suite',
  'Web Server',
  'Security',
  'Development',
  'Collaboration',
  'Cloud Storage',
  'Utilities',
];

type ViewType = 'grid' | 'table';
type SortField = 'name' | 'manufacturer' | 'version_count' | 'cve_count';
type SortOrder = 'asc' | 'desc';

export default function SoftwareCatalog() {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedManufacturers, setSelectedManufacturers] = useState<Set<string>>(new Set());
  const [isOpenSource, setIsOpenSource] = useState<boolean | undefined>(undefined);
  const [hasCVEs, setHasCVEs] = useState<boolean | undefined>(undefined);
  const [softwareType, setSoftwareType] = useState<'application' | 'os' | 'package' | undefined>(undefined);
  const [packageType, setPackageType] = useState<'rpm' | 'deb' | 'apk' | 'pacman' | undefined>(undefined);
  const [showFilters, setShowFilters] = useState(false);
  const [categoriesExpanded, setCategoriesExpanded] = useState(true);
  const [manufacturersExpanded, setManufacturersExpanded] = useState(true);
  const [openSourceExpanded, setOpenSourceExpanded] = useState(true);
  const [cveExpanded, setCveExpanded] = useState(true);
  const [typeExpanded, setTypeExpanded] = useState(true);
  const [packageTypeExpanded, setPackageTypeExpanded] = useState(true);
  const [viewType, setViewType] = useState<ViewType>('table');
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [page, setPage] = useState(1);
  const [allSoftware, setAllSoftware] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);

  // Fetch manufacturers list
  const { data: manufacturersData } = useGetManufacturersQuery();

  // Fetch software with current filters
  const { data, isLoading, isFetching, error } = useGetSoftwareListQuery({
    search: search || undefined,
    category: selectedCategory !== 'All' ? selectedCategory : undefined,
    manufacturer: Array.from(selectedManufacturers),
    is_open_source: isOpenSource,
    has_cves: hasCVEs,
    type: softwareType,
    package_type: packageType,
    sort_by: sortBy,
    sort_order: sortOrder,
    page,
    limit: 25,
  });

  // Reset pagination when filters change
  useEffect(() => {
    setPage(1);
    setAllSoftware([]);
    setHasMore(true);
  }, [search, selectedCategory, selectedManufacturers, isOpenSource, hasCVEs, softwareType, packageType, sortBy, sortOrder]);

  // Append results for infinite scroll
  useEffect(() => {
    if (data?.data) {
      if (page === 1) {
        setAllSoftware(data.data);
      } else {
        // Filter out duplicates by ID before appending
        setAllSoftware((prev) => {
          const existingIds = new Set(prev.map((s) => s.id));
          const newItems = data.data.filter((s: any) => !existingIds.has(s.id));
          return [...prev, ...newItems];
        });
      }

      const totalLoaded = page * 25;
      setHasMore(totalLoaded < (data.total || 0));
    }
  }, [data, page]);

  const loadMore = () => {
    if (!isFetching && hasMore) {
      setPage((prev) => prev + 1);
    }
  };

  const toggleManufacturer = (manufacturer: string) => {
    setSelectedManufacturers((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(manufacturer)) {
        newSet.delete(manufacturer);
      } else {
        newSet.add(manufacturer);
      }
      return newSet;
    });
  };

  const clearAllFilters = () => {
    setSelectedCategory('All');
    setSelectedManufacturers(new Set());
    setIsOpenSource(undefined);
    setHasCVEs(undefined);
    setSoftwareType(undefined);
    setPackageType(undefined);
    setSearch('');
  };

  const activeFilterCount =
    (selectedCategory !== 'All' ? 1 : 0) +
    selectedManufacturers.size +
    (isOpenSource !== undefined ? 1 : 0) +
    (hasCVEs !== undefined ? 1 : 0) +
    (softwareType !== undefined ? 1 : 0) +
    (packageType !== undefined ? 1 : 0);

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const SortIndicator = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? (
      <ChevronUpIcon className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDownIcon className="w-4 h-4 inline ml-1" />
    );
  };

  const manufacturers = useMemo(() => {
    if (!manufacturersData?.data) return [];
    return manufacturersData.data;
  }, [manufacturersData]);

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-severity-critical mb-4">⚠️</div>
        <h2 className="text-xl font-semibold mb-2">Failed to load software catalog</h2>
        <p className="text-gray-400">Please try again later</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-bold mb-4">
          Software <span className="text-gradient">Catalog</span>
        </h1>
        <p className="text-gray-400 text-lg">
          Browse and track {data?.total || 0}+ enterprise software products with automated version monitoring
        </p>
      </div>

      <div className="flex gap-6">
        {/* Desktop Sidebar Filters */}
        <aside className="hidden lg:block flex-shrink-0 w-64">
          <div className="card sticky top-6 h-[calc(100vh-8rem)] flex flex-col">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <h3 className="font-semibold text-lg">Filters</h3>
              {activeFilterCount > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-cyber-blue-400 hover:text-cyber-blue-300 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>

            {/* Scrollable Filter Content */}
            <div className="overflow-y-auto flex-1 pr-2 -mr-2"
              style={{ scrollbarWidth: 'thin' }}
            >
              {/* Category Filter */}
              <div className="mb-6">
                <button
                  onClick={() => setCategoriesExpanded(!categoriesExpanded)}
                  className="flex items-center justify-between w-full text-sm font-medium text-gray-400 mb-3 hover:text-gray-300 transition-colors"
                >
                  <span>Category</span>
                  {categoriesExpanded ? (
                    <ChevronUpIcon className="w-4 h-4" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4" />
                  )}
                </button>
                {categoriesExpanded && (
                  <div className="space-y-2">
                    {categories.map((category) => (
                      <button
                        key={category}
                        onClick={() => setSelectedCategory(category)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          selectedCategory === category
                            ? 'bg-cyber-blue-500 text-white'
                            : 'text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Manufacturer Filter */}
              <div className="mb-6">
                <button
                  onClick={() => setManufacturersExpanded(!manufacturersExpanded)}
                  className="flex items-center justify-between w-full text-sm font-medium text-gray-400 mb-3 hover:text-gray-300 transition-colors"
                >
                  <span>
                    Manufacturer {selectedManufacturers.size > 0 && `(${selectedManufacturers.size})`}
                  </span>
                  {manufacturersExpanded ? (
                    <ChevronUpIcon className="w-4 h-4" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4" />
                  )}
                </button>
                {manufacturersExpanded && (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {manufacturers.map((mfg) => (
                      <label
                        key={mfg.manufacturer}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-700 cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedManufacturers.has(mfg.manufacturer)}
                          onChange={() => toggleManufacturer(mfg.manufacturer)}
                          className="rounded border-gray-600 text-cyber-blue-500 focus:ring-cyber-blue-500 focus:ring-offset-dark-bg"
                        />
                        <span className="text-sm flex-1">{mfg.manufacturer}</span>
                        <span className="text-xs text-gray-500">({mfg.product_count})</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Open Source Filter */}
              <div className="mb-6">
                <button
                  onClick={() => setOpenSourceExpanded(!openSourceExpanded)}
                  className="flex items-center justify-between w-full text-sm font-medium text-gray-400 mb-3 hover:text-gray-300 transition-colors"
                >
                  <span>Open Source</span>
                  {openSourceExpanded ? (
                    <ChevronUpIcon className="w-4 h-4" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4" />
                  )}
                </button>
                {openSourceExpanded && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setIsOpenSource(isOpenSource === true ? undefined : true)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        isOpenSource === true
                          ? 'bg-purple-500 text-white'
                          : 'bg-dark-bg-tertiary text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => setIsOpenSource(isOpenSource === false ? undefined : false)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        isOpenSource === false
                          ? 'bg-purple-500 text-white'
                          : 'bg-dark-bg-tertiary text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      No
                    </button>
                  </div>
                )}
              </div>

              {/* Software Type Filter */}
              <div className="mb-6">
                <button
                  onClick={() => setTypeExpanded(!typeExpanded)}
                  className="flex items-center justify-between w-full text-sm font-medium text-gray-400 mb-3 hover:text-gray-300 transition-colors"
                >
                  <span>Software Type</span>
                  {typeExpanded ? (
                    <ChevronUpIcon className="w-4 h-4" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4" />
                  )}
                </button>
                {typeExpanded && (
                  <div className="space-y-2">
                    <button
                      onClick={() => setSoftwareType(undefined)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        softwareType === undefined
                          ? 'bg-cyber-blue-500 text-white'
                          : 'text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      All Types
                    </button>
                    <button
                      onClick={() => setSoftwareType('application')}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        softwareType === 'application'
                          ? 'bg-cyber-blue-500 text-white'
                          : 'text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      Applications
                    </button>
                    <button
                      onClick={() => setSoftwareType('os')}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        softwareType === 'os'
                          ? 'bg-cyber-blue-500 text-white'
                          : 'text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      Operating Systems
                    </button>
                    <button
                      onClick={() => setSoftwareType('package')}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        softwareType === 'package'
                          ? 'bg-cyber-blue-500 text-white'
                          : 'text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      Packages
                    </button>
                  </div>
                )}
              </div>

              {/* Package Type Filter (only show when type=package) */}
              {softwareType === 'package' && (
                <div className="mb-6">
                  <button
                    onClick={() => setPackageTypeExpanded(!packageTypeExpanded)}
                    className="flex items-center justify-between w-full text-sm font-medium text-gray-400 mb-3 hover:text-gray-300 transition-colors"
                  >
                    <span>Package Type</span>
                    {packageTypeExpanded ? (
                      <ChevronUpIcon className="w-4 h-4" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4" />
                    )}
                  </button>
                  {packageTypeExpanded && (
                    <div className="space-y-2">
                      <button
                        onClick={() => setPackageType(undefined)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          packageType === undefined
                            ? 'bg-purple-500 text-white'
                            : 'text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        All Package Types
                      </button>
                      <button
                        onClick={() => setPackageType('rpm')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          packageType === 'rpm'
                            ? 'bg-purple-500 text-white'
                            : 'text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        RPM (Red Hat/Fedora)
                      </button>
                      <button
                        onClick={() => setPackageType('deb')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          packageType === 'deb'
                            ? 'bg-purple-500 text-white'
                            : 'text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        DEB (Ubuntu/Debian)
                      </button>
                      <button
                        onClick={() => setPackageType('apk')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          packageType === 'apk'
                            ? 'bg-purple-500 text-white'
                            : 'text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        APK (Alpine)
                      </button>
                      <button
                        onClick={() => setPackageType('pacman')}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          packageType === 'pacman'
                            ? 'bg-purple-500 text-white'
                            : 'text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        Pacman (Arch)
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Has CVEs Filter */}
              <div>
                <button
                  onClick={() => setCveExpanded(!cveExpanded)}
                  className="flex items-center justify-between w-full text-sm font-medium text-gray-400 mb-3 hover:text-gray-300 transition-colors"
                >
                  <span>Vulnerabilities</span>
                  {cveExpanded ? (
                    <ChevronUpIcon className="w-4 h-4" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4" />
                  )}
                </button>
                {cveExpanded && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => setHasCVEs(hasCVEs === true ? undefined : true)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        hasCVEs === true
                          ? 'bg-severity-critical text-white'
                          : 'bg-dark-bg-tertiary text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      Has CVEs
                    </button>
                    <button
                      onClick={() => setHasCVEs(hasCVEs === false ? undefined : false)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        hasCVEs === false
                          ? 'bg-green-500 text-white'
                          : 'bg-dark-bg-tertiary text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      No CVEs
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 min-w-0 space-y-6">
          {/* Search and View Toggle */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search software or manufacturer..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="input pl-12"
              />
            </div>

            {/* Mobile Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="btn-secondary flex items-center gap-2 justify-center lg:hidden"
            >
              <FunnelIcon className="w-5 h-5" />
              <span>Filters</span>
              {activeFilterCount > 0 && <span className="badge badge-info ml-2">{activeFilterCount}</span>}
            </button>

            {/* View Toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setViewType('grid')}
                className={`p-2 rounded-lg transition-all ${
                  viewType === 'grid'
                    ? 'bg-cyber-blue-500 text-white'
                    : 'bg-dark-bg-tertiary text-gray-400 hover:bg-gray-700'
                }`}
                title="Grid view"
              >
                <Squares2X2Icon className="w-5 h-5" />
              </button>
              <button
                onClick={() => setViewType('table')}
                className={`p-2 rounded-lg transition-all ${
                  viewType === 'table'
                    ? 'bg-cyber-blue-500 text-white'
                    : 'bg-dark-bg-tertiary text-gray-400 hover:bg-gray-700'
                }`}
                title="Table view"
              >
                <TableCellsIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="card animate-slide-in lg:hidden">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Filters</h3>
                {activeFilterCount > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="text-sm text-cyber-blue-400 hover:text-cyber-blue-300"
                  >
                    Clear all
                  </button>
                )}
              </div>

              {/* Category */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Category</h4>
                <div className="flex flex-wrap gap-2">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setSelectedCategory(category)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        selectedCategory === category
                          ? 'bg-cyber-blue-500 text-white'
                          : 'bg-dark-bg-tertiary text-gray-300 hover:bg-gray-700'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Manufacturer (Top 10) */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">
                  Manufacturer {selectedManufacturers.size > 0 && `(${selectedManufacturers.size})`}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {manufacturers.slice(0, 10).map((mfg) => (
                    <label
                      key={mfg.manufacturer}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-dark-bg-tertiary hover:bg-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedManufacturers.has(mfg.manufacturer)}
                        onChange={() => toggleManufacturer(mfg.manufacturer)}
                        className="rounded border-gray-600 text-cyber-blue-500"
                      />
                      <span className="text-sm">{mfg.manufacturer}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Open Source */}
              <div className="mb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Open Source</h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => setIsOpenSource(isOpenSource === true ? undefined : true)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                      isOpenSource === true
                        ? 'bg-purple-500 text-white'
                        : 'bg-dark-bg-tertiary text-gray-300'
                    }`}
                  >
                    Yes
                  </button>
                  <button
                    onClick={() => setIsOpenSource(isOpenSource === false ? undefined : false)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                      isOpenSource === false
                        ? 'bg-purple-500 text-white'
                        : 'bg-dark-bg-tertiary text-gray-300'
                    }`}
                  >
                    No
                  </button>
                </div>
              </div>

              {/* Has CVEs */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Vulnerabilities</h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => setHasCVEs(hasCVEs === true ? undefined : true)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                      hasCVEs === true
                        ? 'bg-severity-critical text-white'
                        : 'bg-dark-bg-tertiary text-gray-300'
                    }`}
                  >
                    Has CVEs
                  </button>
                  <button
                    onClick={() => setHasCVEs(hasCVEs === false ? undefined : false)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium ${
                      hasCVEs === false ? 'bg-green-500 text-white' : 'bg-dark-bg-tertiary text-gray-300'
                    }`}
                  >
                    No CVEs
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Results count and active filters */}
          <div className="flex items-center justify-between">
            <p className="text-gray-400">
              {isLoading && page === 1 ? (
                'Loading...'
              ) : (
                <>
                  Showing <span className="text-white font-medium">{allSoftware.length}</span> of{' '}
                  <span className="text-white font-medium">{data?.total || 0}</span>{' '}
                  {(data?.total || 0) === 1 ? 'product' : 'products'}
                </>
              )}
            </p>
            {activeFilterCount > 0 && (
              <p className="text-sm text-gray-500">{activeFilterCount} active filters</p>
            )}
          </div>

          {/* Content Area with Infinite Scroll */}
          {isLoading && page === 1 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 skeleton rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <div className="h-5 skeleton w-3/4" />
                      <div className="h-4 skeleton w-1/2" />
                      <div className="flex gap-2">
                        <div className="h-6 skeleton w-16" />
                        <div className="h-6 skeleton w-20" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : allSoftware.length === 0 ? (
            <div className="text-center py-12 card">
              <MagnifyingGlassIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No software found</h3>
              <p className="text-gray-400">Try adjusting your search or filters</p>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={allSoftware.length}
              next={loadMore}
              hasMore={hasMore}
              loader={
                <div className="text-center py-4">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyber-blue-500"></div>
                </div>
              }
              endMessage={
                <p className="text-center text-gray-500 py-4">
                  {allSoftware.length > 0 && 'All products loaded'}
                </p>
              }
            >
              {viewType === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {allSoftware.map((software) => (
                    <Link key={software.id} to={`/software/${software.id}`} className="card-glow group">
                      <div className="flex items-start gap-4 mb-4">
                        {software.icon_url ? (
                          <img src={software.icon_url} alt={software.name} className="w-12 h-12 rounded-lg" />
                        ) : (
                          <div className="w-12 h-12 bg-cyber-blue-500/10 rounded-lg flex items-center justify-center">
                            <ShieldCheckIcon className="w-6 h-6 text-cyber-blue-500" />
                          </div>
                        )}

                        <div className="flex-1">
                          <h3 className="font-semibold mb-1 group-hover:text-cyber-blue-400 transition-colors">
                            {software.name}
                          </h3>
                          <p className="text-sm text-gray-400">{software.manufacturer}</p>
                        </div>
                      </div>

                      {software.description && (
                        <p className="text-sm text-gray-400 mb-4 line-clamp-2">{software.description}</p>
                      )}

                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="badge badge-info text-xs">{software.category}</span>
                        {software.is_open_source && (
                          <span className="badge bg-purple-500/10 text-purple-400 border border-purple-500/20 text-xs">
                            Open Source
                          </span>
                        )}
                        {software.quantum_ready_status === 'ready' && (
                          <span className="badge bg-green-500/10 text-green-400 border border-green-500/20 text-xs">
                            Quantum Ready
                          </span>
                        )}
                        {software.quantum_ready_status === 'at-risk' && (
                          <span className="badge badge-critical text-xs">Quantum At-Risk</span>
                        )}
                      </div>

                      <div className="flex gap-4 text-sm">
                        {software.version_count !== undefined && (
                          <div className="flex items-center gap-1.5 text-gray-400">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                              />
                            </svg>
                            <span className="font-medium text-white">{software.version_count}</span>
                            <span>versions</span>
                          </div>
                        )}
                        {software.cve_count !== undefined && software.cve_count > 0 && (
                          <div className="flex items-center gap-1.5 text-gray-400">
                            <svg
                              className="w-4 h-4 text-severity-critical"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                              />
                            </svg>
                            <span className="font-medium text-severity-critical">{software.cve_count}</span>
                            <span>CVEs</span>
                          </div>
                        )}
                      </div>

                      {software.cpe_vendor && (
                        <div className="mt-4 pt-4 border-t border-gray-700">
                          <div className="text-xs text-gray-500 font-mono">
                            CPE: {software.cpe_vendor}:{software.cpe_product}
                          </div>
                        </div>
                      )}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="card overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-700">
                        <th
                          onClick={() => handleSort('name')}
                          className="text-left py-3 px-4 font-semibold text-sm cursor-pointer hover:bg-gray-800/50 transition-colors"
                        >
                          Name <SortIndicator field="name" />
                        </th>
                        <th
                          onClick={() => handleSort('manufacturer')}
                          className="text-left py-3 px-4 font-semibold text-sm cursor-pointer hover:bg-gray-800/50 transition-colors"
                        >
                          Manufacturer <SortIndicator field="manufacturer" />
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-sm">Category</th>
                        <th
                          onClick={() => handleSort('version_count')}
                          className="text-left py-3 px-4 font-semibold text-sm cursor-pointer hover:bg-gray-800/50 transition-colors"
                        >
                          Versions <SortIndicator field="version_count" />
                        </th>
                        <th
                          onClick={() => handleSort('cve_count')}
                          className="text-left py-3 px-4 font-semibold text-sm cursor-pointer hover:bg-gray-800/50 transition-colors"
                        >
                          CVEs <SortIndicator field="cve_count" />
                        </th>
                        <th className="text-left py-3 px-4 font-semibold text-sm">Info</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allSoftware.map((software) => (
                        <tr
                          key={software.id}
                          className="border-b border-gray-800 hover:bg-gray-800/30 transition-colors"
                        >
                          <td className="py-3 px-4">
                            <Link
                              to={`/software/${software.id}`}
                              className="flex items-center gap-3 hover:text-cyber-blue-400 transition-colors"
                            >
                              {software.icon_url ? (
                                <img src={software.icon_url} alt={software.name} className="w-8 h-8 rounded" />
                              ) : (
                                <div className="w-8 h-8 bg-cyber-blue-500/10 rounded flex items-center justify-center flex-shrink-0">
                                  <ShieldCheckIcon className="w-4 h-4 text-cyber-blue-500" />
                                </div>
                              )}
                              <span className="font-medium">{software.name}</span>
                            </Link>
                          </td>
                          <td className="py-3 px-4 text-gray-400">{software.manufacturer}</td>
                          <td className="py-3 px-4">
                            <span className="badge badge-info text-xs">{software.category}</span>
                          </td>
                          <td className="py-3 px-4">
                            <span className="font-medium text-white">{software.version_count || 0}</span>
                          </td>
                          <td className="py-3 px-4">
                            {software.cve_count > 0 ? (
                              <span className="font-medium text-severity-critical">{software.cve_count}</span>
                            ) : (
                              <span className="text-gray-500">0</span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex gap-1">
                              {software.is_open_source === 1 && (
                                <span className="badge bg-purple-500/10 text-purple-400 border border-purple-500/20 text-xs">
                                  OSS
                                </span>
                              )}
                              {software.quantum_ready_status === 'ready' && (
                                <span className="badge bg-green-500/10 text-green-400 border border-green-500/20 text-xs">
                                  Q
                                </span>
                              )}
                              {software.is_open_source !== 1 && software.quantum_ready_status !== 'ready' && (
                                <span className="text-gray-600">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
}
